
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';

import Autocomplete, { AutocompleteOption } from '@/components/controls/autocomplete.vue';
import Taxonomy from '@/store/models/taxonomy';
import { BeforeAfterImagesActions, BeforeAfterImagesStatus } from '@/store/modules/before-after-images/types';
import BeforeAfterImage from '@/store/modules/before-after-images/before-after-image';
import ProductRelationship from '@/store/modules/products/product-relationship';

import NotificationBanner from '@/components/blocks/notification-banner.vue';
import FormInput from '@/components/controls/form-input.vue';
import ImageUploadControl from '@/components/controls/image-upload.vue';
import Publish from '@/components/forms/publish.vue';
import PracticeSelectionControl from '@/components/controls/practice-selection.vue';
import Checkbox from '@/components/controls/checkbox.vue';
import BeforeAfterLicenseAgreementModal from '@/components/layout/before-after-license-agreement-modal.vue';

export default Vue.extend({
  name: 'EditBeforeAfterImageView',
  inject: {
    $validator: '$validator',
  },
  components: {
    FormInput,
    NotificationBanner,
    Publish,
    Autocomplete,
    ImageUploadControl,
    PracticeSelectionControl,
    Checkbox,
    BeforeAfterLicenseAgreementModal,
  },
  props: {
    add: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      treatmentsSearchInput: '',
      productAccessoriesInput: '',
      productsSearchInput: '',
      showLicenseModal: false,
    };
  },
  computed: {
    ...mapGetters('user/profile', ['isSuperAdmin', 'isAdmin', 'isUser', 'products', 'accessories']),
    /**
     * Before After Images
     */
    ...mapState('before-after-images', ['editable', 'editableStatus']),
    ...mapGetters('before-after-images', {
      isLoading: 'editableIsLoading',
      isSaving: 'editableIsSaving',
      hasUnsavedChanges: 'editableHasUnsavedChanges',
    }),

    /**
     * Products
     */
    ...mapState('products', {
      productsTree: 'tree',
    }),
    /** For non-admin: owned product ids from user profile (owned flag only); used to filter product dropdown. Admin gets undefined (no filter). */
    ownedProductIds(): number[] | undefined {
      if (this.isSuperAdmin || this.isAdmin) {
        return undefined;
      }
      const profileProducts = this.products as { id: number; owned?: boolean }[] | undefined;
      const owned = profileProducts?.filter((p) => p.owned !== false) ?? [];
      return owned.length ? owned.map((p) => p.id) : undefined;
    },
    selectedProductIds(): number[] {
      return (this.editable.products || []).map((p) => p.id);
    },
    /** Products that can be added (from tree, allowed, not already selected), filtered by search. */
    productsOptions(): AutocompleteOption[] {
      if (!this.productsTree.length) return [];
      let list = this.productsTree;
      if (this.ownedProductIds && this.ownedProductIds.length) {
        const allowed = new Set(this.ownedProductIds);
        list = list.filter((p) => allowed.has(p.id));
      }
      const selectedSet = new Set(this.selectedProductIds);
      list = list.filter((p) => !selectedSet.has(p.id));
      const q = (this.productsSearchInput || '').toLowerCase();
      if (q) {
        list = list.filter((p) => (p.title || '').toLowerCase().indexOf(q) > -1);
      }
      return list.map((p) => ({ label: p.title || `#${p.id}`, value: p.id }));
    },
    hasProductsOptions(): boolean {
      return this.productsOptions.length > 0;
    },

    /**
     * Component
     */
    isFetching() {
      return this.isLoading || this.isSaving;
    },
    isInactive() {
      return this.editable.status === BeforeAfterImagesStatus.Inactive;
    },
    /** Treatments from all selected products (merged, deduped by id). */
    treatments(): Taxonomy[] {
      if (!this.editable || !this.editable.products || !this.editable.products.length || !this.productsTree.length) {
        return [];
      }
      const seen = new Set<number>();
      const out: Taxonomy[] = [];
      for (const p of this.editable.products) {
        const product = this.productsTree.find((item) => item.id === p.id);
        if (product && product.treatments) {
          for (const t of product.treatments) {
            if (!seen.has(t.id)) {
              seen.add(t.id);
              out.push(t);
            }
          }
        }
      }
      return out;
    },
    treatmentsTitle(): string {
      return (this.editable.treatments || []).map((t) => t.title).join(', ');
    },
    treatmentsOptions(): AutocompleteOption[] {
      const ids = this.editable.treatments.map(({ id }) => id);

      return this.treatments
        .filter(({ id }) => ids.indexOf(id) === -1)
        .map(({ id, title }) => ({
          label: title,
          value: id,
        }))
        .filter(({ label }) => label.toLowerCase().indexOf(this.treatmentsSearchInput.toLowerCase()) > -1);
    },
    /** For non-admin: accessory ids from user profile (practice-owned); undefined for admin (no filter). */
    ownedAccessoryIds(): number[] | undefined {
      if (this.isSuperAdmin || this.isAdmin) {
        return undefined;
      }
      const profileAccessories = this.accessories as { id: number }[] | undefined;
      return profileAccessories?.length ? profileAccessories.map((a) => a.id) : undefined;
    },
    /** Product accessories from all selected products (merged, deduped); for non-admin filtered to practice-owned only. */
    allProductAccessories(): Taxonomy[] {
      if (!this.editable || !this.editable.products || !this.editable.products.length || !this.productsTree.length) {
        return [];
      }
      const seen = new Set<number>();
      const out: Taxonomy[] = [];
      for (const p of this.editable.products) {
        const product = this.productsTree.find((item) => item.id === p.id);
        const accessories = product && product.accessories ? product.accessories : [];
        for (const a of accessories) {
          if (!seen.has(a.id)) {
            seen.add(a.id);
            out.push(a);
          }
        }
      }
      if (this.ownedAccessoryIds && this.ownedAccessoryIds.length) {
        const ownedSet = new Set(this.ownedAccessoryIds);
        return out.filter((a) => ownedSet.has(a.id));
      }
      return out;
    },
    getProductAccessories(): Taxonomy[] {
      const ids = this.selectedProductAccessories.map(({ id }) => id);
      return this.allProductAccessories.filter(({ id }) => ids.indexOf(id) === -1);
    },
    getProductAccessoriesOptions(): AutocompleteOption[] {
      return this.getProductAccessories
        .map((accessory) => ({
          label: accessory.title,
          value: accessory.id,
        }))
        .filter(({ label }) => label.toLowerCase().indexOf(this.productAccessoriesInput.toLowerCase()) > -1);
    },
    hasProductAccessories(): boolean {
      return Boolean(this.allProductAccessories.length);
    },
    hasProductAccessoriesOptions(): boolean {
      return Boolean(this.getProductAccessories.length);
    },
    selectedProductAccessories(): Taxonomy[] {
      const availableIds = this.allProductAccessories.map(({ id }) => id);
      return (this.editable.accessories || []).filter(({ id }) => availableIds.indexOf(id) !== -1);
    },
  },
  methods: {
    updateNumberOfTreatments(value: string) {
      const num = value === '' ? null : parseInt(value, 10);
      this.update({
        numberOfTreatmentsPerformed: Number.isNaN(num) ? null : num,
      });
    },
    ...mapActions('before-after-images', {
      update: BeforeAfterImagesActions.UPDATE_EDITABLE,
      save: BeforeAfterImagesActions.SAVE_EDITABLE,
      removeAction: BeforeAfterImagesActions.DELETE,
      restoreAction: BeforeAfterImagesActions.RESTORE,
    }),
    addProduct(option: AutocompleteOption) {
      const productId = Number(option.value);
      if (!productId || this.selectedProductIds.indexOf(productId) !== -1) return;
      const product = this.productsTree.find((item) => item.id === productId);
      if (!product) return;
      const products = [...(this.editable.products || []), ProductRelationship.clone(product)];
      this.filterTreatmentsAndAccessoriesAfterProductChange(products);
    },
    removeProduct(productId: number) {
      const products = (this.editable.products || []).filter((p) => p.id !== productId);
      this.update({ products, product: products[0] });
      this.filterTreatmentsAndAccessoriesAfterProductChange(products);
    },
    /** Keep only treatments/accessories that still belong to the current product set. */
    filterTreatmentsAndAccessoriesAfterProductChange(products: ProductRelationship[]) {
      const update: {
        products?: ProductRelationship[];
        product?: ProductRelationship | null;
        treatments?: Taxonomy[];
        accessories?: Taxonomy[];
        title?: string;
      } = { products, product: products.length ? products[0] : null };
      const treatments = this.treatmentsFromProducts(products);
      const accessoriesPool = this.accessoriesFromProducts(products);
      const treatmentIds = new Set(treatments.map((t) => t.id));
      const accessoryIds = new Set(accessoriesPool.map((a) => a.id));
      const validTreatments = (this.editable.treatments || []).filter((t) => treatmentIds.has(t.id));
      const validAccessories = (this.editable.accessories || []).filter((a) => accessoryIds.has(a.id));
      if (validTreatments.length !== (this.editable.treatments || []).length) {
        update.treatments = validTreatments;
        update.title = validTreatments.length ? validTreatments.map((t) => t.title).join(', ') : '';
      }
      if (validAccessories.length !== (this.editable.accessories || []).length) {
        update.accessories = validAccessories;
      }
      this.update(update);
    },
    treatmentsFromProducts(products: ProductRelationship[]): Taxonomy[] {
      if (!products.length || !this.productsTree.length) return [];
      const seen = new Set<number>();
      const out: Taxonomy[] = [];
      for (const p of products) {
        const product = this.productsTree.find((item) => item.id === p.id);
        if (product && product.treatments) {
          for (const t of product.treatments) {
            if (!seen.has(t.id)) {
              seen.add(t.id);
              out.push(t);
            }
          }
        }
      }
      return out;
    },
    accessoriesFromProducts(products: ProductRelationship[]): Taxonomy[] {
      if (!products.length || !this.productsTree.length) return [];
      const seen = new Set<number>();
      const out: Taxonomy[] = [];
      for (const p of products) {
        const product = this.productsTree.find((item) => item.id === p.id);
        if (product && product.accessories) {
          for (const a of product.accessories) {
            if (!seen.has(a.id)) {
              seen.add(a.id);
              out.push(a);
            }
          }
        }
      }
      return out;
    },
    addProductAccessory(option: AutocompleteOption) {
      const accessories = [...(this.editable.accessories || [])];
      const accessory = this.allProductAccessories.find((item) => item.id === option.value);
      if (!accessory) {
        return;
      }
      accessories.push(Taxonomy.clone(accessory));
      this.update({ accessories });
    },
    removeProductAccessory(accessoryId: number) {
      const accessories = (this.editable.accessories || []).filter(({ id }) => id !== accessoryId);
      this.update({ accessories });
    },
    addTreatment(option: AutocompleteOption) {
      const treatment = this.treatments.find((item) => item.id === option.value);
      if (!treatment) {
        return;
      }

      const treatments = [...this.editable.treatments];

      treatments.push(Taxonomy.clone(treatment));

      this.update({ treatments, title: treatments.map((t) => t.title).join(', ') });
    },
    removeTreatment(treatmentId: number) {
      const treatments = this.editable.treatments.filter(({ id }) => id !== treatmentId);

      this.update({ treatments, title: treatments.map((t) => t.title).join(', ') });
    },
    remove() {
      this.removeAction({ id: this.editable.id });
    },
    restore() {
      this.restoreAction({ id: this.editable.id });
    },
    updateImageData(data: Partial<BeforeAfterImage>) {
      const image = BeforeAfterImage.clone(this.editable.images[0]);

      Object.keys(data).forEach((key) => {
        image[key] = data[key];
      });

      this.update({ images: [image] });
    },
  },
  watch: {
    'editable.treatments': {
      handler(treatments) {
        if (!treatments || !treatments.length) return;
        const newTitle = treatments.map((t) => t.title).join(', ');
        if (this.editable.title !== newTitle) {
          this.update({ title: newTitle });
        }
      },
      immediate: true,
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      const answer = window.confirm(this.$t('Do you really want to leave? You have unsaved changes!'));

      if (!answer) {
        next(false);
        return;
      }
    }

    next();
  },
});
